import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {debounce} from 'lodash';
import {getModelForResourceType} from 'resources/modelRegistry';
import {history, pushQueryParams, removeQueryParams} from 'client/history';
import {ERROR_TYPES} from 'client/constants';
import {downloadCSVTemplate} from 'client/components/CreateAccounts/shared';

import {
  addToast,
  Anchor,
  Button,
  Grid,
  LoadingSpinner,
  NoDataMsg,
  SearchInput,
  Table,
  Text
} from '@albert-io/atomic';

import {getSchoolAccountsQuery} from './shared';
import SchoolAccountsBanners from './SchoolAccountsBanners/SchoolAccountsBanners.react';
import SchoolAccountsTable from './SchoolAccountsTable/SchoolAccountsTable.react';

import './school-accounts.scss';

export default class SchoolAccounts extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    location: PropTypes.shape({
      query: PropTypes.object
    })
  };

  constructor(props) {
    super(props);

    this.state = {
      isReady: false,
      school: null
    };
  }

  componentDidMount() {
    this.bootstrapSchoolAccounts();
  }

  componentDidUpdate() {
    if (!this.state.isReady) {
      this.bootstrapSchoolAccounts();
    }
  }

  schoolQuery = () => getSchoolAccountsQuery(this.props.params.schoolId);

  bootstrapSchoolAccounts = async () => {
    const query = this.schoolQuery();
    try {
      const school = await query.getResourcePromise();

      this.setState({
        isReady: true,
        school
      });
    } catch (e) {
      query.invalidateInterest();
      addToast({
        title: 'Something went wrong',
        type: 'danger',
        message: 'There was a problem loading your roster. If the problem persists, contact us.'
      });
      history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
    }
  };

  handleEnrollmentSettingsChange = () => {
    this.schoolQuery().invalidateInterest();
    this.setState({isReady: false});
  };

  setSearchTerm = debounce((e, searchTerm) => {
    pushQueryParams({search: searchTerm});
  }, 500);

  resetSearchTerm = () => removeQueryParams('search');

  render() {
    if (!this.state.isReady) {
      return (
        <Grid.Container className='u-mar-t_7'>
          <Grid.Row>
            <Grid.Col all={12}>
              <LoadingSpinner size={2} />
              <Text
                as='div'
                className='u-mar-lr_auto u-mar-tb_0 u-text-align_center'
                emphasis='low'
                size='s'
              >
                Gathering details about your school...
              </Text>
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      );
    }

    const {school} = this.state;
    const emailDomains = school.getEmailDomains();
    const showManagedAccounts =
      !school.getManagedUsers().isEmpty() ||
      school.getClassrooms().some((classroom) => !classroom.getStudents().isEmpty());

    if (showManagedAccounts) {
      return (
        <Grid.Container className='u-mar-t_5'>
          <Grid.Row justify='center'>
            {this.state.isReady && (
              <Grid.Col as='aside' className='school-accounts__banners u-mar-b_4' m={12} all={4}>
                <Button
                  as={Link}
                  className='u-width_100pc u-mar-b_1'
                  to={`/school-admin/${this.props.params.schoolId}/create-accounts`}
                >
                  Create school accounts
                </Button>
                <Button
                  className='u-width_100pc u-mar-b_1'
                  onClick={downloadCSVTemplate}
                  variant='text'
                >
                  Download CSV template
                </Button>
                <SchoolAccountsBanners
                  onChange={this.handleEnrollmentSettingsChange}
                  school={school}
                />
              </Grid.Col>
            )}
            <Grid.Col m={12} all={8}>
              <Table.TitleBar
                inputGroup={
                  <SearchInput
                    defaultValue={this.props.location.query?.search}
                    placeholder='Search...'
                    onChange={this.setSearchTerm}
                    onReset={this.resetSearchTerm}
                  />
                }
                title='Students'
              />
              <SchoolAccountsTable {...this.props} />
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      );
    }
    return (
      <Grid.Container className='u-mar-t_5'>
        <Grid.Row as='aside' justify='center'>
          <Grid.Col className='u-mar-b_6' s={12} all={7}>
            <SchoolAccountsBanners onChange={this.handleEnrollmentSettingsChange} school={school} />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row justify='center'>
          <Grid.Col s={12} all={7}>
            <NoStudentsMsg emailDomains={emailDomains} {...this.props} />
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>
    );
  }
}

const NoStudentsMsg = ({emailDomains, params}) => {
  if (emailDomains.size > 0) {
    return (
      <NoDataMsg.Container centered className='school-accounts__no-data-msg u-mar-lr_auto'>
        <NoDataMsg.Heading className='u-mar-lr_auto u-mar-b_2'>
          Let&apos;s get started!
        </NoDataMsg.Heading>
        <NoDataMsg.Body className='u-mar-lr_auto'>
          <Text as='p' className='u-mar-b_2' size='l'>
            To create student accounts, click the button below and upload a properly formatted CSV
            file.
          </Text>
          <Text as='p' className='u-mar-b_5' size='l'>
            Download our CSV template{' '}
            <Anchor
              as='a'
              onClick={downloadCSVTemplate}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  downloadCSVTemplate();
                }
              }}
              role='button'
              tabIndex={0}
            >
              here
            </Anchor>
            .
          </Text>
          <Button
            as={Link}
            className='u-mar-b_1'
            to={`/school-admin/${params.schoolId}/create-accounts`}
          >
            Create school accounts
          </Button>
        </NoDataMsg.Body>
      </NoDataMsg.Container>
    );
  }

  return (
    <NoDataMsg.Container centered className='school-accounts__no-data-msg u-mar-lr_auto'>
      <NoDataMsg.Heading className='u-mar-lr_auto'>
        Your school has not claimed any domains
      </NoDataMsg.Heading>
      <NoDataMsg.Body className='u-mar-lr_auto'>
        {' '}
        Your school has the option to create and manage student accounts. To learn more, review this{' '}
        <Anchor
          as='a'
          rel='noopener noreferrer'
          target='_blank'
          href='https://help.albert.io/en/articles/3847312-what-are-school-managed-email-domains'
        >
          help article
        </Anchor>{' '}
        or reach out to{' '}
        <Anchor as='a' href='mailto:success@albert.io'>
          success@albert.io
        </Anchor>
        .
      </NoDataMsg.Body>
    </NoDataMsg.Container>
  );
};

NoStudentsMsg.propTypes = {
  emailDomains: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(getModelForResourceType('email_domain_v1'))
  ),
  params: PropTypes.object
};
